// @ts-strict-ignore
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import omitBy from 'lodash/omitBy';

import {getEventValueAndLabel} from 'common/report/util/events';
import {findKeyByValue} from 'common/util/typescript';
import {ALL_MATH_TYPES, DATASET_MIXPANEL, ResourceType, SPECIAL_EVENTS} from 'common/report/constants';
import {TOP_PEOPLE} from 'common/query-builder/query-block/properties-menu/constants';
import {
  CohortItem,
  FiltersOperator,
  isEventBasedEntry,
  isEventEntry,
  isFormulaEntry,
  isInlineCohortItem,
  isMetricEntry,
  isSavedCohortItem,
  MetricEntry,
  MetricEntryBehavior,
  MetricEntryMeasurement,
  QueryCohortEntry,
  QueryFormulaEntry,
  QueryMetricEntry,
  QuerySuperEntry,
} from 'common/query-builder/query-entry-section/types';
import {MetricMeasurement} from 'common/report/insights/util/types';
import {BehaviorShowClause, ShowClause} from 'common/report/insights/models/show-clause';
import {FilterClause} from 'common/report/insights/models/filter-clause';
import {ShowSection} from 'common/report/insights/models/section';
import {formatSource} from 'common/report/insights/util';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {BirthIntervalUnit} from 'common/report/retention/util/types';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {SectionItem} from 'common/components/items-menu/types';
import {getRetentionBucketName} from 'common/report/retention/util';
import {
  FUNNELS_MATH_TYPES,
  InsightsResourceType,
  MetricType,
  PER_USER_AGGREGATIONS,
  PRIMITIVE_MATH_TYPES,
  RetentionType,
  TopLevelMetricType,
} from 'common/types/reports/bookmark';
import {BehaviorsResource} from 'common/report/model-resources/behaviors';
import {
  convertBookmarkPropertyToPropertySelection,
  convertPropertySelectiontoBookmarkProperty,
  createRetentionMetricShowClause,
  getMetricEntryBehavior,
  isSavedBehavior,
  transformFunnelEntryToMetricBehavior,
} from 'common/query-builder/query-entry-section/row-types/metric-entry/metrics/metric-entry-util';
import {EventItem} from 'common/query-builder/query-block/events-menu/types';
import {MetricsResource} from 'common/report/model-resources/metrics';
import {NthTimeOperator} from 'common/widgets/property-filter-menu/models/types';
import {DisplaySize, Placement} from 'common/components/tooltip/types';

import {QUERY_BUILDER_RESOURCE_TYPE_MAPPING} from '../models/constants';
import {isBehaviorShowClause, isFormulaShowClause} from '../models/utils';
import {ReportSectionsMetadata, ShowClause as ShowClauseType} from '../util/types';
import {DETERMINER_MAPPING} from './constants';
import {BehaviorMetric, InsightsQueryError} from './types';

/** operators in the legacy per-user format */
export const LEGACY_PER_USER_OPERATORS = [
  ALL_MATH_TYPES.AVERAGE,
  ALL_MATH_TYPES.MEDIAN,
  ALL_MATH_TYPES.MAX,
  ALL_MATH_TYPES.MIN,
  ALL_MATH_TYPES.P90,
  ALL_MATH_TYPES.P75,
  ALL_MATH_TYPES.P25,
];

export function getDataGroupLabel(dataGroupId, dataGroups: any[] = [], opts?: {useDefault?: boolean; all?: boolean}) {
  const group = dataGroups.find((item) => item.groupID === dataGroupId);

  return group
    ? formatSource(group.displayName, {all: opts?.all ?? true, pluralizeDataGroup: true})
    : opts?.useDefault === false
      ? null
      : TOP_PEOPLE.label;
}

/**
 * Transforms show clauses to event entries
 */
export function getEntries({
  showClauses,
  dataGroups = [],
  queryError,
  dateUnit,
  reportSectionsMetadata,
}: {
  showClauses: ShowClauseType[];
  behaviorsResource?: BehaviorsResource;
  metricsResource?: MetricsResource;
  dataGroups?: DataGroup[];
  queryError?: InsightsQueryError;
  dateUnit?: TimeUnit;
  reportSectionsMetadata?: ReportSectionsMetadata;
}): Array<QueryMetricEntry | MetricEntry> {
  // TODO opslead
  const entries: Array<QueryMetricEntry | MetricEntry> = [];
  let formulaIdx = 0;
  const formulaErrorByIdx = new Map<number, string>(
    (queryError?.formula_errors || []).map(({index, message}) => [index, message]),
  );
  showClauses.forEach((clause: ShowClauseType, index: number) => {
    if (isFormulaShowClause(clause)) {
      const {rolling, cumulative, multiAttribution} = clause.measurement ?? {};
      const error = formulaErrorByIdx.get(formulaIdx);
      const entry: QueryFormulaEntry = {
        ...(clause._idx ? {_idx: clause._idx} : {}),
        display: clause.display,
        isHidden: clause.isHidden ?? false,
        isExpanded: clause.isExpanded ?? false,
        type: ResourceType.Formula,
        event: omitBy({...clause, error}, isNil),
        filters: [],
        rolling,
        cumulative,
        multiAttribution,
      };
      entries.push(entry);
      formulaIdx += 1;
      return;
    }

    const {behavior, measurement} = clause;

    if (behavior.type === MetricType.Funnel) {
      const metricBehavior: MetricEntryBehavior = getMetricEntryBehavior(behavior);
      const metricEntry: MetricEntry = {
        ...(clause._idx ? {_idx: clause._idx} : {}),
        ...(clause.id ? {id: clause.id} : {}),
        ...(clause.name ? {name: clause.name} : {}),
        type: TopLevelMetricType.MetricEntry,
        display: clause.display,
        isHidden: clause.isHidden ?? false,
        isExpanded: clause.isExpanded ?? false,
        behavior: metricBehavior,
        measurement: getFunnelMetricMeasurement(clause as BehaviorMetric),
      };

      const hasUnsavedChanges = behavior?.hasUnsavedChanges;

      entries.push({
        ...metricEntry,
        ...(hasUnsavedChanges ? {hasUnsavedChanges} : {}),
      });
      return;
    }
    if (behavior.type === MetricType.Retention || behavior.type === MetricType.RetentionFrequency) {
      const metricBehavior: MetricEntryBehavior = getMetricEntryBehavior(behavior);
      const metricMeasurement: MetricEntryMeasurement = getRetentionMetricMeasurement({
        clause: clause as BehaviorMetric,
        index,
        dateUnit,
        reportSectionsMetadata,
      });
      const metricEntry: MetricEntry = {
        ...(clause._idx ? {_idx: clause._idx} : {}),
        ...(clause.id ? {id: clause.id} : {}),
        ...(clause.name ? {name: clause.name} : {}),
        type: TopLevelMetricType.MetricEntry,
        display: clause.display,
        isHidden: clause.isHidden ?? false,
        isExpanded: clause.isExpanded ?? false,
        behavior: metricBehavior,
        measurement: metricMeasurement,
      };

      const hasUnsavedChanges = behavior?.hasUnsavedChanges;

      entries.push({
        ...metricEntry,
        ...(hasUnsavedChanges ? {hasUnsavedChanges} : {}),
      });
      return;
    }

    const type = findKeyByValue(QUERY_BUILDER_RESOURCE_TYPE_MAPPING, behavior.resourceType) || ResourceType.Event;

    let props: QueryMetricEntry = {
      ...(clause._idx ? {_idx: clause._idx} : {}),
      type: null,
      display: clause.display,
      isHidden: clause.isHidden ?? false,
      isExpanded: clause.isExpanded ?? false,
      aggregationOperator: measurement.math,
      aggregationOperatorPerUser: measurement.perUserAggregation,
      dataGroupId: behavior.dataGroupId,
      property: null,
      event: {
        custom: behavior.type === MetricType.CustomEvent,
        id: behavior.id,
        renamed: behavior.renamed,
      } as EventItem,
      filters: behavior.filters
        ? behavior.filters.map((filterClause) => new FilterClause(filterClause).toPropertyFilter())
        : [],
      filtersOperator: behavior.filters?.length
        ? findKeyByValue(DETERMINER_MAPPING, behavior.filtersDeterminer)
        : FiltersOperator.And,
      readOnlyEvent: type === ResourceType.User,
      ...(measurement.math === ALL_MATH_TYPES.HISTOGRAM ? {customBucket: behavior.customBucket} : {}),
      ...(measurement.multiAttribution ? {multiAttribution: measurement.multiAttribution} : {}),
      ...(measurement.rolling ? {rolling: measurement.rolling} : {}),
      ...(measurement.cumulative ? {cumulative: measurement.cumulative} : {}),
      ...(measurement.segmentMethod ? {countUsersOnceType: measurement.segmentMethod} : {}),
      ...(measurement.dataGroupId ? {dataGroupId: measurement.dataGroupId} : {}),
      ...(measurement.percentile ? {percentile: measurement.percentile} : {}),
    };

    const {type: behaviorType, id, raw_cohort: rawCohort, name, dataGroupId, renamed} = behavior;
    switch (type) {
      case ResourceType.Cohort: {
        const inlineCohort = rawCohort;
        const cohortItem: CohortItem = inlineCohort
          ? {
              resourceType: ResourceType.Cohort,
              label: name,
              value: {
                cohort: {
                  raw_cohort: rawCohort,
                  name,
                },
              },
            }
          : {
              resourceType: ResourceType.Cohort,
              label: name,
              value: id,
              ...(renamed ? {renamed} : {}),
              ...(dataGroupId ? {data_group_id: dataGroupId} : {}),
            };
        const queryCohortProps: QueryCohortEntry = {
          ...props,
          type: ResourceType.Cohort,
          event: cohortItem,
        };
        props = queryCohortProps;
        break;
      }
      case ResourceType.User:
      case ResourceType.Event: {
        let {value: eventValue, label} = getEventValueAndLabel({
          name: behaviorType === MetricType.CustomEvent ? null : name,
          custom: behaviorType === MetricType.CustomEvent,
          id,
        });
        // get user type dataGroupId label
        if (type === ResourceType.User) {
          if (dataGroupId) {
            label = getDataGroupLabel(dataGroupId, dataGroups || []);
            eventValue = TOP_PEOPLE.value;
          } else {
            label = TOP_PEOPLE.label;
            eventValue = TOP_PEOPLE.value;
          }
        }

        // Top events queries look across all events, hence clear out eventValue
        // otherwise the pseudo event value of "$top_events" will be passed on as
        // the event value, resulting in 0 matches
        if (eventValue === SPECIAL_EVENTS.TOP_EVENTS) {
          eventValue = undefined;
          label = SPECIAL_EVENTS.TOP_EVENTS;
        }

        props.type = type;
        props.event = {
          custom: behaviorType === MetricType.CustomEvent,
          label: eventValue ? label : ``,
          value: eventValue,
          ...(renamed ? {renamed} : {}),
          ...(id ? {id} : {}),
        };
        break;
      }
    }

    props.property = convertBookmarkPropertyToPropertySelection(measurement.property, behavior);

    // migrate old aggregation format
    if (
      !measurement.property &&
      // this is not in the type def, so doing some hack
      LEGACY_PER_USER_OPERATORS.includes(props.aggregationOperator as (typeof LEGACY_PER_USER_OPERATORS)[number])
    ) {
      props.aggregationOperatorPerUser = PER_USER_AGGREGATIONS.TOTAL;
    }

    entries.push(props);
  });

  return entries;
}

/**
 * Rather given entry behavior has been modified or not
 */
/**
 * Transforms metric entries to show clauses within a show section
 */
export function getShowSection({
  entries,
  dataGroupId,
  attributionSelection,
  queryType,
}: {
  entries: Array<QuerySuperEntry | MetricEntry>;
  dataGroupId?: string;
  attributionSelection?: number | BehaviorShowClause[`measurement`][`actionMode`];
  queryType?: MetricType;
}): ShowSection {
  let showSection: ShowSection;

  if (entries.length) {
    const showClauses = entries
      .map((entry) => {
        if (isFormulaEntry(entry)) {
          const {id, name, definition, referencedMetrics} = entry.event;
          return ShowClause.createShowClause({
            ...(entry._idx ? {_idx: entry._idx} : {}),
            isHidden: entry.isHidden ?? false,
            isExpanded: entry.isExpanded ?? false,
            display: entry.display,
            measurement: {
              rolling: entry.rolling,
              cumulative: entry.cumulative,
              multiAttribution: entry.multiAttribution,
            },
            type: TopLevelMetricType.Formula,
            ...omitBy({id, name, definition, referencedMetrics}, isNil),
          });
        }

        /** @todo (pablo) this whole block of code will be simplified/updated to work with metrics only */
        if (isMetricEntry(entry)) {
          if (entry.behavior.type === MetricType.Funnel) {
            return createFunnelMetricShowClause(entry, attributionSelection);
          }
          if (entry.behavior.type === MetricType.Retention) {
            const metricType = queryType ?? MetricType.Retention;
            return createRetentionMetricShowClause(metricType, entry, attributionSelection);
          }
          if (entry.behavior.type === MetricType.RetentionFrequency) {
            const metricType = queryType ?? MetricType.RetentionFrequency;
            return createRetentionMetricShowClause(metricType, entry, attributionSelection);
          }
          return ShowClause.createShowClause({
            ...(entry._idx ? {_idx: entry._idx} : {}),
            isHidden: entry.isHidden ?? false,
            isExpanded: entry.isExpanded ?? false,
            display: entry.display,
            measurement: entry.measurement,
            type: TopLevelMetricType.Metric,
          });
        }

        const {
          event,
          filters,
          display,
          property,
          filtersOperator,
          aggregationOperator,
          percentile,
          type,
          aggregationOperatorPerUser,
          rolling,
          cumulative,
          countUsersOnceType,
        } = entry;

        const filterClauses = filters.map((filter) => FilterClause.fromPropertyFilter(filter));

        let showFilterSection = {};
        if (filterClauses.length) {
          showFilterSection = {
            filters: filterClauses,
            filtersDeterminer: DETERMINER_MAPPING[filtersOperator],
          };
        }

        const attrs: BehaviorMetric = {
          ...(entry._idx ? {_idx: entry._idx} : {}),
          ...(entry.id ? {id: entry.id} : {}),
          ...(entry.name ? {name: entry.name} : {}),
          type: TopLevelMetricType.Metric,
          isHidden: entry.isHidden ?? false,
          isExpanded: entry.isExpanded ?? false,
          behavior: {
            type: MetricType.Event,
            resourceType: null,
            dataset: DATASET_MIXPANEL,
            filters: [],
            ...showFilterSection,
            ...(isEventEntry(entry) && aggregationOperator === ALL_MATH_TYPES.HISTOGRAM
              ? {customBucket: entry.customBucket}
              : {}),
            // dataGroupId goes in measurement for non-cohort / profile entries
            ...(!isEventBasedEntry(entry) && (entry.dataGroupId ? {dataGroupId: entry.dataGroupId} : {})),
          },
          display,
          measurement: {
            math: aggregationOperator || PRIMITIVE_MATH_TYPES.TOTAL,
            perUserAggregation: (aggregationOperatorPerUser as PER_USER_AGGREGATIONS) || null,
            rolling: rolling || null,
            cumulative: cumulative || false,
            segmentMethod: countUsersOnceType,
            property: null,
            ...(isEventEntry(entry) && entry.multiAttribution ? {multiAttribution: entry.multiAttribution} : {}),
            // dataGroupId goes in behavior for cohort / profile entries
            ...(isEventBasedEntry(entry) && (entry.dataGroupId ? {dataGroupId: entry.dataGroupId} : {})),
            // include percentile value for custom percentile aggregation
            ...(aggregationOperator === PRIMITIVE_MATH_TYPES.CUSTOM_PERCENTILE ? {percentile} : {}),
          },
        };

        switch (type) {
          case ResourceType.Cohort: {
            attrs.behavior.type = MetricType.Cohort;
            attrs.behavior.resourceType = InsightsResourceType.Cohorts;
            if (isInlineCohortItem(event)) {
              const cohortValue = event.value.cohort;

              attrs.behavior.name = event.label;
              attrs.behavior.raw_cohort = cohortValue.raw_cohort;
            } else if (isSavedCohortItem(event)) {
              attrs.behavior.id = event.value;
              attrs.behavior.name = event.label;
              attrs.behavior.renamed = event.renamed;
            }
            break;
          }
          case ResourceType.User:
          case ResourceType.Event:
            if (type === ResourceType.Event && event.custom) {
              attrs.behavior.type = MetricType.CustomEvent;
            }
            if (type === ResourceType.Event && !event.custom) {
              attrs.behavior.type = MetricType.Event;
            }
            if (type === ResourceType.User) {
              attrs.behavior.type = MetricType.People;
            }
            attrs.behavior.resourceType = QUERY_BUILDER_RESOURCE_TYPE_MAPPING[type];
            attrs.behavior.name = event.value;
            attrs.behavior.id = event.id;
            attrs.behavior.renamed = event.renamed;
            break;
        }

        if (property) {
          attrs.measurement.property = convertPropertySelectiontoBookmarkProperty(property);
        }
        return attrs;
      })
      .map((clause) => {
        const hasFirstTimeFilter =
          isBehaviorShowClause(clause) &&
          clause.behavior.filters?.some((filter) => filter.filterOperator === NthTimeOperator.NthTime);

        const uniqueMathTypes = [FUNNELS_MATH_TYPES.UNIQUE, FUNNELS_MATH_TYPES.CONVERSION_RATE_UNIQUE];
        const isExcludedMathType =
          isBehaviorShowClause(clause) &&
          !uniqueMathTypes.includes((clause.measurement as MetricMeasurement).math as FUNNELS_MATH_TYPES);

        let measurement = clause.measurement;

        if ((hasFirstTimeFilter || isExcludedMathType) && !isEmpty(clause.measurement)) {
          measurement = {
            ...clause.measurement,
            segmentMethod: null,
          };
        }

        const sanitizedClause = {
          ...clause,
          measurement,
        } as ShowClauseType;

        return sanitizedClause;
      })
      .map((clause) => {
        return ShowClause.createShowClause(clause);
      });

    showSection = new ShowSection(showClauses);
  } else {
    showSection = ShowSection.createShowSection({
      behavior: {
        dataGroupId,
        type: MetricType.Event,
        // Cleanup: Missing resourceType
        resourceType: undefined,
      },
    });
  }

  return showSection;
}

export function createFunnelMetricShowClause(
  entry: MetricEntry,
  attributionSelection: number | BehaviorShowClause[`measurement`][`actionMode`],
  forceExpand: boolean = false,
) {
  const behavior =
    !forceExpand && isSavedBehavior(entry) && !entry.hasUnsavedChanges
      ? {
          id: entry.behavior.id,
          type: entry.behavior.type,
          name: entry.behavior.name,
        }
      : transformFunnelEntryToMetricBehavior(entry.behavior);
  // attributionSelection is now set on the entry measurement for report_consistency instead
  // of being set directly on the query builder update selection
  const entryAttributionSelection = attributionSelection || entry.measurement.attributionSelection;
  const measurement: BehaviorShowClause[`measurement`] = {
    // TODO: change to funnelStepIndex
    stepIndex: entry.measurement.stepIndex,
    math: entry.measurement.aggregationOperator || FUNNELS_MATH_TYPES.CONVERSION_RATE_UNIQUE,
    perUserAggregation: null,
    rolling: null,
    cumulative: false,
    ...(entryAttributionSelection
      ? {
          actionMode: typeof entryAttributionSelection === `number` ? `step` : entryAttributionSelection,
          actionStep: typeof entryAttributionSelection === `number` ? entryAttributionSelection : null,
        }
      : {}),
    segmentMethod: entry.measurement.countUsersOnceType,
    property: entry.measurement.property?.value
      ? convertPropertySelectiontoBookmarkProperty(entry.measurement.property)
      : (entry.measurement[`property`] as any), // TODO the typing here is messed up
    multiAttribution: entry.measurement.multiAttribution,
    ...(entry.measurement.dataGroupId ? {dataGroupId: entry.measurement.dataGroupId} : {}),
    // include percentile value for custom percentile aggregation
    ...(entry.measurement.percentile ? {percentile: entry.measurement.percentile} : {}), // TODO: this is not in the type def
  };
  return ShowClause.createShowClause({
    ...(entry._idx ? {_idx: entry._idx} : {}),
    ...(entry.id ? {id: entry.id} : {}),
    ...(entry.name ? {name: entry.name} : {}),
    isHidden: entry.isHidden ?? false,
    isExpanded: entry.isExpanded ?? false,
    display: entry.display,
    behavior,
    measurement,
  });
}

function generateRetentionBuckets({
  showIndex,
  retentionBucketIndex,
  retentionType,
  retentionUnit,
  dateUnit,
  retentionCustomBucketSizes,
  reportSectionsMetadata,
}: {
  showIndex: number;
  retentionBucketIndex: number;
  retentionType: RetentionType;
  retentionUnit: TimeUnit;
  dateUnit: BirthIntervalUnit;
  retentionCustomBucketSizes: number[];
  reportSectionsMetadata: ReportSectionsMetadata;
}): Array<SectionItem<number>> {
  let items: Array<SectionItem<number>> = [];
  const retentionBucketsSize = reportSectionsMetadata?.show[showIndex]?.retention_buckets_size ?? 0;
  if (retentionBucketsSize > 0) {
    items = new Array(retentionBucketsSize).fill(null).map((_, i) => {
      return {
        label: getRetentionBucketName({
          bucketIndex: i,
          retentionType,
          retentionUnit,
          dateUnit,
          bucketSizes: retentionCustomBucketSizes,
        }),
        value: i,
        isSelected: i === retentionBucketIndex,
      };
    });
  } else {
    items = [
      {
        label: getRetentionBucketName({
          bucketIndex: retentionBucketIndex,
          retentionType,
          retentionUnit,
          dateUnit,
          bucketSizes: retentionCustomBucketSizes,
        }),
        value: 0,
        isSelected: true,
      },
    ];
  }

  return items;
}

export function getMetricEntryMeasurement({
  clause,
  index,
  dateUnit,
  reportSectionsMetadata,
}: {
  clause: BehaviorMetric;
  index: number;
  dateUnit?: TimeUnit;
  reportSectionsMetadata?: ReportSectionsMetadata;
}): MetricEntryMeasurement {
  const {behavior, measurement} = clause;

  if (behavior.type === MetricType.Funnel) {
    return getFunnelMetricMeasurement(clause);
  } else if (behavior.type === MetricType.Retention || behavior.type === MetricType.RetentionFrequency) {
    return getRetentionMetricMeasurement({clause, index, dateUnit, reportSectionsMetadata});
  }

  return {
    property: convertBookmarkPropertyToPropertySelection(measurement.property, behavior),
    aggregationOperator: measurement.math,
    rolling: measurement.rolling,
    aggregationOperatorPerUser: measurement.perUserAggregation,
    cumulative: Boolean(measurement.cumulative),
    countUsersOnceType: measurement.segmentMethod ? measurement.segmentMethod : null,
    multiAttribution: measurement.multiAttribution,

    ...(measurement.dataGroupId ? {dataGroupId: measurement.dataGroupId} : {}),
    ...(measurement.percentile ? {percentile: measurement.percentile} : {}),
  };
}

function getFunnelMetricMeasurement({behavior, measurement}: BehaviorMetric): MetricEntryMeasurement {
  return {
    property: convertBookmarkPropertyToPropertySelection(measurement.property, behavior),
    aggregationOperator: measurement.math,
    cumulative: Boolean(measurement.cumulative),
    // funnel step
    stepIndex: measurement.stepIndex,
    countUsersOnceType: measurement.segmentMethod ? measurement.segmentMethod : null,
    attributionSelection: measurement.actionMode === `step` ? measurement.actionStep : measurement.actionMode,
    multiAttribution: measurement.multiAttribution,
    ...(measurement.dataGroupId ? {dataGroupId: measurement.dataGroupId} : {}),
    ...(measurement.percentile ? {percentile: measurement.percentile} : {}),
  };
}

function getRetentionMetricMeasurement({
  clause,
  index,
  dateUnit,
  reportSectionsMetadata,
}: {
  clause: BehaviorMetric;
  index: number;
  dateUnit?: TimeUnit;
  reportSectionsMetadata?: ReportSectionsMetadata;
}): MetricEntryMeasurement {
  const {behavior, measurement} = clause;
  return {
    property: convertBookmarkPropertyToPropertySelection(measurement.property, behavior),
    aggregationOperator: measurement.math,
    retentionCumulative: Boolean(measurement.retentionCumulative),
    retentionBucketIndex: measurement.retentionBucketIndex,
    retentionBuckets: generateRetentionBuckets({
      showIndex: index,
      retentionBucketIndex: measurement.retentionBucketIndex,
      retentionType: behavior.type === MetricType.Retention ? `birth` : `addiction`,
      retentionUnit: behavior.retentionUnit,
      dateUnit,
      retentionCustomBucketSizes: behavior.retentionCustomBucketSizes,
      reportSectionsMetadata,
    }),
    countUsersOnceType: measurement.segmentMethod ? measurement.segmentMethod : null,
    attributionSelection: measurement.retentionSegmentationEvent,
    multiAttribution: measurement.multiAttribution,
    ...(measurement.dataGroupId ? {dataGroupId: measurement.dataGroupId} : {}),
  };
}

export function getAllEventsDisabledTooltip(reason: string | undefined) {
  if (!reason) {
    return undefined;
  }
  return {
    placement: Placement.Left,
    size: DisplaySize.Medium,
    text: reason,
  };
}
